import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { HANDIN_PROVIDER } from '@core/assignments/constants';
import * as ANALYTICS from '@core/constants/analytics';
import showToast from '@core/toasts/operations/showToast.operation';

import handinSubmissionsExport from './handinSubmissionsExport';
import { useAssignmentTypeProvider } from './AssignmentTypeProvider';
import { analyticsTrackEvent } from '@core/utils/analytics';
import { DOWNLOAD_SUBMISSIONS } from '@app/constants/texts';
import {
  Icons,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';

export interface SubmissionsDownloadButtonProps {
  currentUserIsEducator: boolean;
  assignmentExternalId: string;
  accessToken: string;
  isGroupAssignment: boolean;
  isAnonymisedAssignment: boolean;
  submissionCount: number;
  styles: any;
}

const SubmissionsDownloadButton = (props: SubmissionsDownloadButtonProps) => {
  const { currentUserIsEducator, submissionCount, styles } = props;
  const { type } = useAssignmentTypeProvider();
  const [isRequested, setIsRequested] = useState(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get the text for accessiblity and tooltip
  const context = useMemo(() => {
    const errorMessage = 'There are no submissions to download yet';
    const disabled = submissionCount === 0;
    const tooltip = disabled
      ? errorMessage
      : 'Only files attached to submissions are downloaded';
    const ariaLabel = disabled
      ? `It is not possible to download all submissions for this assignment: ${errorMessage.toLowerCase()}.`
      : 'Download all submissions for this assignment.';

    return { disabled, ariaLabel, tooltip };
  }, [submissionCount]);

  if (type !== HANDIN_PROVIDER) {
    return null;
  }

  if (!currentUserIsEducator) {
    return null;
  }

  const requestDownloadAll = async () => {
    try {
      const { accessToken, assignmentExternalId } = props;
      if (!accessToken || !assignmentExternalId) {
        return;
      }
      analyticsTrackEvent(ANALYTICS.BULK_DOWNLOAD, {
        assignmentId: assignmentExternalId,
        itemId: assignmentExternalId,
        itemType: ANALYTICS.ASSIGNMENT_ITEM_TYPE,
      });
      console.info('Requesting download of all submissions', {
        assignmentExternalId,
      });
      setIsRequested(true);
      dispatch(
        showToast({
          message:
            "Preparing to download files attached to submissions. Once completed, you'll receive an email with a link to download the ZIP file.",
        })
      );
      await handinSubmissionsExport(accessToken, assignmentExternalId);
    } catch {
      dispatch(
        showToast({
          emoji: '🤔',
          message: 'There was an error while preparing submissions.',
        })
      );
      setIsRequested(false);
    }
  };

  return (
    <>
      <Tooltip
        disableInteractive
        title={context.tooltip}
        disableFocusListener={isMobile || isRequested || !context.disabled}
        disableHoverListener={isMobile || isRequested || !context.disabled}
        disableTouchListener={isMobile || isRequested || !context.disabled}
      >
        <Button
          type="button"
          kind="secondary"
          sx={styles.purpleButton}
          onClick={requestDownloadAll}
          aria-label={context.ariaLabel}
          disabled={isRequested || context.disabled}
        >
          <Icons.DownloadOutlined fontSize="small" aria-hidden />
          {DOWNLOAD_SUBMISSIONS}
        </Button>
      </Tooltip>
    </>
  );
};

export default SubmissionsDownloadButton;
